import React, { useState } from "react";

import { css } from "@emotion/css";

import Logo from "@/assets/icons/logo-steto-light-full.svg?url";
import { LoginChannel } from "@/auth/loginFlow.ts";
import { useStyles } from "@/hooks/useStyles";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/card/Card.tsx";
import { LoginChoice } from "@/uiKit/organisms/LoginChoice.tsx";
import { LoginCodeForm } from "@/uiKit/organisms/LoginCodeForm.tsx";
import { LoginIdentifierForm } from "@/uiKit/organisms/LoginIdentifierForm.tsx";

type LoginStep = "choice" | "identifier" | "code";

/**
 * The three steps of the login flow, on one page.
 * `choice` shows both audiences side by side; the form steps drop the columns
 * and stand alone in the middle so nothing competes with the field in focus.
 */
export const Login: React.FC = () => {
  const styles = useStyles(makeStyles);

  const [step, setStep] = useState<LoginStep>("choice");
  const [channel, setChannel] = useState<LoginChannel>("email");
  const [contact, setContact] = useState("");

  return (
    <div className={styles.container}>
      <img className={styles.logo} src={Logo} alt="Steto" />

      {step === "choice" ? (
        <LoginChoice onConnect={() => setStep("identifier")} />
      ) : (
        <Card className={styles.card}>
          {step === "identifier" ? (
            <LoginIdentifierForm
              initialChannel={channel}
              initialContact={contact}
              onCancel={() => setStep("choice")}
              onCodeSent={(sentChannel, sentContact) => {
                setChannel(sentChannel);
                setContact(sentContact);
                setStep("code");
              }}
            />
          ) : (
            <LoginCodeForm channel={channel} contact={contact} onChangeContact={() => setStep("identifier")} />
          )}
        </Card>
      )}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(16, 16),
    width: "100%",
    minHeight: "100vh",
    gap: theme.spacing(24),
  }),
  logo: css({
    width: "min(80vw, 300px)",
    margin: theme.spacing(0, 24, 24, 24),
  }),
  card: css({
    width: theme.spacing(256),
    maxWidth: "100%",
  }),
});
